import { type AuditLogFilters, type AuditLogResponse, getAuditLogs } from '@api/audit-logs-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

interface UseAuditLogsResult {
  logs: AuditLogResponse[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAuditLogs(filters?: AuditLogFilters): UseAuditLogsResult {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['audit-logs', filters] as const, [filters]);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getAuditLogs(filters);
      return response.data;
    },
  });

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    logs: data ?? [],
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load audit logs') : null,
    refetch,
  };
}
